import { Link } from 'react-router-dom';
import { Phone, ArrowRight, ShieldCheck, Clock, BadgeEuro, Droplets, Wrench, Pipette, MapPin, CheckCircle2, ChevronRight, Star } from 'lucide-react';
import { SEO } from '../components/SEO';
import { locations } from '../data/locations';
import { services } from '../data/services';
import { generateFAQs } from '../data/faqs';
import { images } from '../data/images';
import { useImages } from '../hooks/useImages';
import { ReviewsSection } from '../components/reviews/ReviewsSection';
import { TrustIndicators } from '../components/TrustIndicators';

export function Home() {
  const { getServiceImage, getHeroImage } = useImages();

  const groupedLocations = locations.reduce((acc, loc) => {
    if (!acc[loc.region]) acc[loc.region] = [];
    acc[loc.region].push(loc);
    return acc;
  }, {} as Record<string, typeof locations>);

  const serviceIcons: Record<string, any> = {
    'rohrreinigung': Droplets,
    'klempner-notdienst': Wrench,
    'sanitaer': Pipette,
  };

  const allFaqs = generateFAQs('Rohrreinigung & Sanitär', 'Ihrer Nähe');
  const faqs = allFaqs.flatMap(category => category.questions).slice(0, 6);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "König Rohrreinigung",
      "url": "https://konig-rohrreinigung-klempner-notdienst.de/",
      "logo": "https://konig-rohrreinigung-klempner-notdienst.de/logo.png",
      "telephone": "+4915219576394",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "1248"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+4915219576394",
        "contactType": "customer service",
        "availableLanguage": "German"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "url": "https://konig-rohrreinigung-klempner-notdienst.de/",
      "name": "König Rohrreinigung",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://konig-rohrreinigung-klempner-notdienst.de/standorte?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    }
  ];

  return (
    <>
      <SEO 
        title="Rohrreinigung & Klempner Notdienst" 
        description="Ihre Experten für Rohrreinigung und Sanitär Notdienst. 24/7 in Berlin, Hamburg, Hannover und NRW für Sie im Einsatz. Königliche Qualität, feste Preise."
        canonicalUrl="/"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative bg-brand-black text-white min-h-[90vh] flex items-center pt-24 pb-32 overflow-hidden">
        {/* Background Image with elegant overlay */}
        <div className="absolute inset-0">
           <picture>
              <source media="(max-width: 768px)" srcSet="/images/hero-home-mobile.webp" />
              <img referrerPolicy="no-referrer" 
                src={getHeroImage('home')} 
                alt="König Rohrreinigung Experte"
                fetchPriority="high" 
                className="w-full h-full object-cover"
              />
           </picture>
            <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/90 to-brand-black/70"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 z-10 w-full text-center">
          <div 
            className="inline-block bg-white/5 px-6 py-2 rounded-full text-brand-gold text-sm font-bold uppercase tracking-widest mb-8 backdrop-blur-md border border-brand-gold/20 shadow-[0_0_30px_rgba(197,160,89,0.15)] animate-fadeIn"
          >
            Deutschlandweiter 24/7 Notdienst
          </div>
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold mb-8 leading-[1.1] max-w-5xl mx-auto tracking-tight animate-fadeInUp"
          >
            Königliche Qualität. <br/><span className="text-brand-red inline-block mt-2">Sofortige Hilfe.</span>
          </h1>
          <p 
            className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed font-light animate-fadeIn"
          >
            Ihr zertifizierter Fachbetrieb für Rohrreinigung, Klempnerarbeiten und Sanitär. Schnell vor Ort, transparente Festpreise.
          </p>
          <div 
            className="flex flex-col sm:flex-row justify-center gap-6 animate-fadeInUp"
          >
            <a href="tel:+4915219576394" className="group flex items-center justify-center gap-4 bg-brand-red hover:bg-brand-red-dark text-white px-10 py-5 rounded-xl font-bold text-lg transition-all shadow-[0_0_40px_rgba(200,16,46,0.4)] hover:shadow-[0_0_60px_rgba(200,16,46,0.6)] hover:-translate-y-1">
              <Phone className="w-6 h-6 animate-pulse" />
              Notruf: +49 152 19576394
            </a>
            <Link to="/leistungen" className="flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 backdrop-blur-md text-white px-10 py-5 rounded-xl font-bold text-lg transition-all border border-white/10 hover:-translate-y-1">
              Unsere Leistungen ansehen
            </Link>
          </div>

          <div 
            className="mt-20 flex flex-wrap justify-center gap-8 md:gap-16 text-gray-500 text-sm font-medium uppercase tracking-widest"
          >
            <div className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-brand-gold" /> Geprüfte Meister</div>
            <div className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-brand-gold" /> 45+ Standorte</div>
            <div className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-brand-gold" /> Keine Anfahrtskosten*</div>
          </div>
        </div>
      </section>

      <TrustIndicators />

      {/* Main Services (Elevated Cards) */}
      <section className="py-24 bg-brand-gray-light relative">
        {/* Decorative background shape */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-white rounded-l-[100px] opacity-50 z-0"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div 
            className="text-center mb-20 animate-fadeInUp"
          >
            <span className="text-brand-red font-bold uppercase tracking-wider text-sm mb-2 block">Unsere Expertise</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-brand-black">Dienstleistungen auf höchstem Niveau</h2>
            <p className="text-gray-600 max-w-2xl mx-auto font-light text-lg">Professionelle Lösungen für jedes Problem rund um Rohr und Sanitär, mit modernster Technik und geschultem Personal.</p>
          </div>
          
          <div 
            className="grid md:grid-cols-3 gap-10"
          >
            {services.map((service, index) => {
              const IconComponent = serviceIcons[service.slug] || Droplets;
              return (
                <div 
                  key={service.id} 
                  className="h-full block animate-fadeIn"
                >
                  <Link 
                    to={`/leistungen/${service.slug}`}
                    className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all overflow-hidden border border-gray-100 flex flex-col h-full hover:-translate-y-2 duration-300"
                  >
                  <div className="relative h-48 overflow-hidden bg-brand-gray-light">
                     <img referrerPolicy="no-referrer" loading="lazy" 
                       src={getServiceImage(service.slug, 600)}
                       alt={service.name}
                       className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                     />
                     <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                     <div className="absolute bottom-4 left-6 bg-brand-red p-3 rounded-lg shadow-lg">
                       <IconComponent className="w-8 h-8 text-white" strokeWidth={1.5} />
                     </div>
                  </div>
                  
                  <div className="p-8 flex flex-col flex-grow">
                    <h3 className="text-2xl font-bold mb-4 text-brand-black">{service.name}</h3>
                    <p className="text-gray-600 mb-8 font-light leading-relaxed flex-grow">{service.shortDescription}</p>
                    
                    <div className="mt-auto border-t border-gray-100 pt-6">
                      <div className="flex items-center justify-between text-brand-black font-bold group-hover:text-brand-red transition-colors">
                        <span>Details ansehen</span>
                        <ArrowRight className="w-5 h-5 transform group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features & Trust */}
      <section className="bg-white py-24 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
         <div className="grid md:grid-cols-2 gap-16 items-center">
             <div 
               className="animate-fadeIn"
             >
               <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">Warum König die beste Wahl ist</h2>
               <p className="text-gray-600 font-light text-lg mb-10 leading-relaxed">
                 In der Branche der Rohrreinigung gibt es leider viele schwarze Schafe. Wir grenzen uns ganz bewusst ab: durch absolute Transparenz, echte Fachkompetenz und einen Service, der den Namen "König" verdient.
               </p>
               
               <div className="space-y-6">
                 <div className="flex gap-4">
                   <div className="w-12 h-12 rounded-2xl bg-brand-red/5 flex items-center justify-center shrink-0">
                     <Clock className="w-6 h-6 text-brand-red" />
                   </div>
                   <div>
                     <h3 className="font-bold text-lg mb-1">Echte 24/7 Erreichbarkeit</h3>
                     <p className="text-gray-500 font-light">Wir sind immer erreichbar. Ein Anruf genügt und der Monteur macht sich auf den Weg.</p>
                   </div>
                 </div>
                 <div className="flex gap-4">
                   <div className="w-12 h-12 rounded-2xl bg-brand-red/5 flex items-center justify-center shrink-0">
                     <BadgeEuro className="w-6 h-6 text-brand-red" />
                   </div>
                   <div>
                     <h3 className="font-bold text-lg mb-1">Garantiert feste Preise</h3>
                     <p className="text-gray-500 font-light">Sie erfahren den Preis vor Arbeitsbeginn. Keine bösen Überraschungen.</p>
                   </div>
                 </div>
                 <div className="flex gap-4">
                   <div className="w-12 h-12 rounded-2xl bg-brand-red/5 flex items-center justify-center shrink-0">
                     <ShieldCheck className="w-6 h-6 text-brand-red" />
                   </div>
                   <div>
                     <h3 className="font-bold text-lg mb-1">Zertifizierte Meisterqualität</h3>
                     <p className="text-gray-500 font-light">Ausgebildete Experten mit modernster Technologie (z.B. TV-Kanal-Inspektion).</p>
                   </div>
                 </div>
               </div>
             </div>
             <div className="relative animate-fadeIn">
                <img referrerPolicy="no-referrer" loading="lazy" 
                  src={images.content.customerSatisfaction} 
                  alt="Zufriedener Kunde"
                  className="rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.15)] object-cover h-[600px] w-full"
                />
                <div className="absolute -bottom-10 -left-10 bg-brand-black text-white p-8 rounded-3xl shadow-2xl max-w-xs animate-fadeInUp">
                   <div className="flex gap-1 mb-3 text-brand-gold">
                     <Star fill="currentColor" className="w-5 h-5" />
                     <Star fill="currentColor" className="w-5 h-5" />
                     <Star fill="currentColor" className="w-5 h-5" />
                     <Star fill="currentColor" className="w-5 h-5" />
                     <Star fill="currentColor" className="w-5 h-5" />
                   </div>
                   <p className="font-bold text-lg mb-2">"Hervorragender Service!"</p>
                   <p className="text-sm text-gray-400 font-light leading-relaxed">
                     "Innerhalb von 30 Minuten vor Ort, Problem kompetent gelöst und der Preis war genau wie vorher besprochen. Absolut empfehlenswert."
                   </p>
                   <p className="text-xs font-bold uppercase tracking-wider mt-4 text-brand-red">— Familie Schmidt, Berlin</p>
                </div>
             </div>
           </div>
        </div>
      </section>

      {/* The 45 Locations Matrix - Hub View */}
      <section className="py-24 bg-brand-gray-light">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-brand-red font-bold uppercase tracking-wider text-sm mb-2 block">Immer in Ihrer Nähe</span>
            <h2 className="text-4xl font-heading font-bold mb-4 text-brand-black">Unsere lokalen Standorte</h2>
            <p className="text-gray-600 max-w-2xl mx-auto font-light text-lg">Wählen Sie Ihre Stadt oder Region, um direkt zu Ihrem lokalen Notdienst-Experten zu gelangen.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {Object.entries(groupedLocations).map(([region, locs], index) => {
              const bgColors = [
                'bg-blue-50',
                'bg-green-50',
                'bg-purple-50',
                'bg-orange-50',
                'bg-teal-50',
                'bg-indigo-50'
              ];
              const borderColor = [
                'border-blue-200',
                'border-green-200',
                'border-purple-200',
                'border-orange-200',
                'border-teal-200',
                'border-indigo-200'
              ];
              const headerColor = [
                'text-blue-900',
                'text-green-900',
                'text-purple-900',
                'text-orange-900',
                'text-teal-900',
                'text-indigo-900'
              ];
              
              const currentBg = bgColors[index % bgColors.length];
              const currentBorder = borderColor[index % borderColor.length];
              const currentHeader = headerColor[index % headerColor.length];

              return (
                <div key={region} className={`${currentBg} p-8 rounded-2xl shadow-lg border ${currentBorder} relative overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}>
                  <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                    <MapPin className="w-32 h-32 text-brand-black" />
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-8">
                      <Link to={`/standorte/${region.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`px-6 py-2 rounded-full border-2 ${currentBorder} ${currentHeader} font-bold text-lg hover:bg-white transition-colors uppercase tracking-wider shadow-sm`}>
                         {region}
                      </Link>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                      {locs.map(loc => (
                        <div key={loc.id} className="border-b border-black/5 pb-4 last:border-0 sm:last:border-b-0">
                          <Link to={`/standorte/${region.toLowerCase().replace(/[^a-z0-9]+/g, '-')}/${loc.slug}`} className={`font-bold ${currentHeader} flex items-center gap-2 mb-2 hover:opacity-70 transition-opacity`}>
                            <MapPin className="w-4 h-4" />
                            {loc.name}
                          </Link>
                          <div className="flex flex-col gap-2 pl-6">
                            {services.map(service => (
                              <Link 
                                key={`${service.id}-${loc.id}`}
                                to={`/leistungen/${service.slug}/${loc.slug}`}
                                className="text-xs text-gray-600 hover:text-brand-red font-medium flex items-center justify-between group/link"
                              >
                                <span>{service.name}</span>
                                <ChevronRight className="w-3 h-3 text-gray-400 group-hover/link:text-brand-red group-hover/link:translate-x-1 transition-all" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection />

      {/* FAQ Section */}
      <section className="py-24 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-brand-black">Häufig gestellte Fragen</h2>
            <p className="text-gray-600 text-lg font-light">Wir beantworten die wichtigsten Fragen rund um unsere Dienstleistungen.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="group bg-brand-gray-light rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-lg text-brand-black hover:text-brand-red transition-colors">
                  {faq.q}
                  <span className="transition group-open:rotate-180">
                    <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24"><path d="M6 9l6 6 6-6"></path></svg>
                  </span>
                </summary>
                <div className="p-6 pt-0 text-gray-600 font-light leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
